/* ──────────────────────────────────────────────
 *  Tunnel VTC — labels par props (miroir de BookingLabels/DEFAULT_LABELS).
 *  /book est hors provider next-intl : aucun useTranslations ici — les
 *  libellés arrivent par props avec des défauts français.
 * ────────────────────────────────────────────── */

import type { AddressObject } from '@cfixe-monorepo/shared';
import {
  DEFAULT_PAYMENT_TIMELINE_LABELS,
  type PaymentTimelineLabels,
} from '../booking/components/shared/payment-timeline-labels';
import { DEFAULT_LABELS } from '../booking/types';

/**
 * Sélection du volet « Type de course » de l'étape trajet :
 * - `'any'` : « Peu importe » (défaut en accès direct) — ni `prestation_id` ni `vehicle_id`
 *   envoyés, le backend affecte le véhicule le moins cher disponible ;
 * - `{ vehicleId }` : véhicule précis (mode véhicules — `config.vehicles` présent) —
 *   `vehicle_id` envoyé au devis, au pré-check de dispo ET au checkout (cohérence fingerprint) ;
 * - `{ prestationId }` : formule verrouillée (mode vitrine « Réserver ce trajet » :
 *   `initialTrip.prestation_id`/deep-link `?rp=`) — chip de rappel non modifiable. C'est aussi la
 *   forme produite par le sélecteur de formules du mode FALLBACK (`config.vehicles` absent :
 *   comportement historique) — `prestation_id` envoyé, jamais `vehicle_id`.
 * - `{ vehicles }` : composition explicite multi-véhicules (« 2 vans + 1 berline »), produite par
 *   les sélecteurs de quantité — `vehicles: [{ vehicle_id, quantity }]` envoyé. Forme accessible
 *   UNIQUEMENT quand le multi est actif (`multi_vehicle_enabled` + plafond ≥ 2) ; une composition
 *   entièrement à zéro retombe sur `'any'` (carte « Automatique »).
 */
export type RideTypeSelection =
  | 'any'
  | { vehicleId: number }
  | { prestationId: number }
  | { vehicles: Array<{ vehicleId: number; quantity: number }> };

/**
 * Trajet par défaut injecté par l'hôte à l'ouverture du tunnel course — typiquement depuis une
 * carte « Course » (type RIDE) du tunnel de réservation classique (callback onSelectRidePrestation).
 * Appliqué à l'init ET quand la référence de la prop change (nouvelle carte cliquée).
 * Une adresse `*_locked` n'est pas modifiable par le client (produit type navette) : champ
 * désactivé + mention « Adresse fixe ».
 */
export interface RideInitialTrip {
  /** Formule (prestation RIDE) présélectionnée — validée contre les formules du pro au chargement. */
  prestation_id?: number;
  pickup_address?: AddressObject;
  dropoff_address?: AddressObject;
  pickup_locked?: boolean;
  dropoff_locked?: boolean;
}

// Les labels de la timeline de paiement/remboursement sont partagés avec le booking classique.
export interface RideLabels extends PaymentTimelineLabels {
  /* Étapes / titres */
  title: string;
  step_quote_title: string;
  step_payment_title: string;
  contact_title: string;

  /* Trajet */
  pickup_address: string;
  pickup_placeholder: string;
  dropoff_address: string;
  dropoff_placeholder: string;
  pickup_datetime: string;
  return_datetime: string;
  trip_type: string;
  trip_one_way: string;
  trip_round_trip: string;
  passengers: string;
  luggage: string;
  small_luggage: string;
  large_luggage: string;
  /** Sélecteur de formules de course (prestations RIDE, pools de véhicules). */
  prestation: string;
  /** Pill « peu importe » : le système affecte le véhicule le moins cher disponible. */
  any_formula: string;
  /** Sélecteur de véhicule (accès direct, `config.vehicles` présent). */
  vehicle: string;
  /** Pill « peu importe » du sélecteur de VÉHICULE (défaut en accès direct). */
  any_vehicle: string;
  /** Chip de rappel non modifiable de la formule choisie (mode vitrine « Réserver ce trajet »). */
  formula_reminder: string;
  /** Dépli / repli de la description rich text de la formule (tronquée à 3 lignes). */
  read_more: string;
  read_less: string;

  /* Multi-véhicules (rendus UNIQUEMENT quand `multi_vehicle_enabled` et plafond ≥ 2). */
  /** Carte de tête du sélecteur en mode multi : le serveur répartit le groupe (`allow_split`). */
  vehicle_auto: string;
  /** Aide sous le sélecteur en mode multi : composer soi-même ou laisser faire. */
  vehicle_auto_hint: string;
  /** Titre du bloc « Véhicules (N) » de l'étape devis. Avec interpolation {count}. */
  vehicles_title: string;
  /** Pré-check : le groupe voyagera dans plusieurs véhicules. Avec interpolation {count}. */
  slot_multi_vehicle_notice: string;
  /** Cause `GROUP_TOO_LARGE` du pré-check : aucune composition ne peut accueillir le groupe. */
  slot_group_too_large: string;
  /** Cause `NO_DRIVER` du pré-check quand le groupe demande plusieurs véhicules. */
  slot_not_enough_drivers: string;
  /** Refus inline : la composition choisie ne peut pas accueillir le groupe saisi. */
  error_composition_capacity: string;
  /** Refus inline : plus de véhicules que le nombre de passagers ({max} interpolé). */
  error_composition_too_many_vehicles: string;
  /**
   * Refus : la somme des MINIMUMS (pax_min / small_luggage_min / large_luggage_min) de la composition
   * dépasse le groupe, sans qu'il y ait plus de véhicules que de passagers — à l'ajout d'un véhicule
   * comme à « Voir le prix »
   * ou au pré-check (p. ex. après une baisse des passagers).
   */
  error_composition_below_minimum: string;
  /**
   * Pré-check : composition explicite refusée faute d'unités de ces classes chez le pro à cette
   * heure (cause NO_VEHICLE) — changer d'horaire n'y fera souvent rien.
   */
  error_composition_exceeds_fleet: string;
  /** Refus inline : plafond de véhicules par réservation atteint ({max} interpolé). */
  error_max_vehicles: string;
  /** Pré-check de dispo (consultatif, non bloquant) à la saisie de l'heure. */
  slot_unavailable: string;
  slot_unavailable_outbound: string;
  slot_unavailable_return: string;
  /** Causes temporelles (reason du pré-check) : hors horaires d'ouverture, délai min, horizon. */
  slot_outside_opening_hours: string;
  slot_outside_opening_hours_return: string;
  slot_too_soon: string;
  slot_too_far: string;
  /** Cause `NO_DRIVER` du pré-check : aucun chauffeur disponible sur le créneau. */
  slot_no_driver: string;
  /** Surbooking accepté par le pro : flotte saturée mais la demande sera transmise (note informative). */
  slot_overbooked_notice: string;
  choose: string;
  selected: string;
  compute_price: string;
  address_search_hint: string;
  /** Mention sous un champ adresse verrouillé par le pro (trajet par défaut d'une carte « Course »). */
  address_locked: string;
  decrease_quantity: string;
  increase_quantity: string;

  /* Estimation */
  estimated_price: string;
  distance: string;
  /** Avec interpolation {distance}. */
  distance_value: string;
  duration: string;
  route_price: string;
  approach_fees: string;
  waiting_fees: string;
  handling_fees: string;
  map_unavailable: string;

  /* Navigation */
  continue: string;
  back: string;
  edit_contact_info: string;

  /* Contact */
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  note: string;
  /** Titre de la section des champs personnalisés du pro (questionnaire) — libellé du tunnel classique. */
  additional_info: string;
  /** Erreur sous un champ personnalisé requis non renseigné — libellé du tunnel classique. */
  required_field: string;

  /* Paiement */
  pay: string;
  go_to_payment: string;
  request_ride: string;
  /** Avec interpolation {amount}. */
  deposit_notice: string;
  /** Avec interpolation {amount}. */
  imprint_notice: string;
  card_save_notice: string;
  secure_payment: string;
  payment_methods_accepted: string;
  finalizing: string;
  verifying: string;
  retry: string;
  /** Retour de redirection Stripe (Klarna…). */
  payment_redirect_failed: string;
  payment_redirect_pending: string;
  payment_redirect_unverified: string;
  payment_redirect_expired: string;

  /* Récapitulatif (TunnelSummary) */
  summary_ride_label: string;
  selection_title: string;
  total_label: string;
  item_singular: string;
  item_plural: string;
  show_detail: string;
  hide_detail: string;
  close: string;
  /** Bouton « i » d'une tuile véhicule : ouvre la modale de détail (photos + description). */
  vehicle_details: string;

  /* Succès / erreurs */
  success_title: string;
  success_subtitle: string;
  request_success_title: string;
  request_success_subtitle: string;
  pending_confirmation_title: string;
  pending_confirmation_subtitle: string;
  recap_title: string;
  pickup_label: string;
  dropoff_label: string;
  pickup_time_label: string;
  return_time_label: string;
  recap_note: string;
  reference: string;
  paid_online: string;
  deposit_paid: string;
  imprint_saved: string;
  card_saved: string;
  pay_to_driver: string;
  pay_on_site: string;
  pay_on_site_or_online: string;
  error_generic: string;
  error_addresses_required: string;
  error_datetime_past: string;
  error_return_required: string;
  error_return_before_pickup: string;
  error_return_before_outbound_end: string;
  error_vehicle_capacity: string;
  /** Refus inline à la saisie : incrément passagers au-delà de la capacité de la sélection ({max} interpolé). */
  error_max_passengers: string;
  /** Refus inline à la saisie : incrément bagages au-delà de la capacité de la sélection ({max} interpolé). */
  error_max_small_luggage: string;
  error_max_large_luggage: string;
  /** Refus inline à la saisie : véhicule/formule trop petit pour les passagers/bagages déjà saisis. */
  error_selection_capacity: string;
  /** Adresse de prise en charge hors zone desservie : refus à la sélection d'adresse + pré-check. */
  error_pickup_outside_zone: string;
  unavailable: string;
}

export const DEFAULT_RIDE_LABELS: RideLabels = {
  ...DEFAULT_PAYMENT_TIMELINE_LABELS,
  title: 'Réserver une course',
  step_quote_title: 'Votre estimation',
  step_payment_title: 'Paiement',
  contact_title: 'Vos coordonnées',

  pickup_address: 'Adresse de départ',
  pickup_placeholder: "Saisissez l'adresse de départ",
  dropoff_address: "Adresse d'arrivée",
  dropoff_placeholder: "Saisissez l'adresse d'arrivée",
  pickup_datetime: 'Date et heure de prise en charge',
  return_datetime: 'Date et heure de retour',
  trip_type: 'Type de trajet',
  trip_one_way: 'Aller simple',
  trip_round_trip: 'Aller-retour',
  passengers: 'Passagers',
  luggage: 'Bagages',
  small_luggage: 'Petits bagages',
  large_luggage: 'Grands bagages',
  prestation: 'Type de course',
  any_formula: 'Peu importe',
  vehicle: 'Véhicule',
  any_vehicle: 'Peu importe',
  formula_reminder: 'Formule sélectionnée',
  read_more: 'Lire la suite',
  read_less: 'Réduire',

  vehicle_auto: 'Automatique',
  vehicle_auto_hint:
    'Choisissez vos véhicules et leur nombre, ou laissez « Automatique » : nous répartissons votre groupe.',
  vehicles_title: 'Véhicules ({count})',
  slot_multi_vehicle_notice: 'Votre groupe voyagera dans {count} véhicules.',
  slot_group_too_large:
    'Ce groupe est trop grand pour les véhicules disponibles chez ce professionnel. Réduisez le nombre de passagers ou de bagages.',
  slot_not_enough_drivers:
    "Il n'y a pas assez de chauffeurs disponibles sur ce créneau pour votre groupe. Choisissez un autre horaire.",
  error_composition_capacity:
    'Les véhicules choisis ne peuvent pas accueillir votre groupe. Ajoutez un véhicule ou réduisez le nombre de passagers ou de bagages.',
  error_composition_too_many_vehicles: 'Vous ne pouvez pas choisir plus de véhicules que de passagers ({max}).',
  error_composition_below_minimum:
    "Ce choix de véhicules demande plus de passagers ou de bagages que votre groupe n'en compte.",
  error_composition_exceeds_fleet:
    "Il n'y a pas assez de véhicules de ce type disponibles à cette heure : laissez « Automatique » ou choisissez un autre horaire.",
  error_max_vehicles: 'Vous pouvez réserver au maximum {max} véhicules en une fois.',

  slot_unavailable: "Aucun véhicule n'est disponible sur ce créneau. Choisissez un autre horaire.",
  slot_unavailable_outbound:
    "Aucun véhicule n'est disponible à cette heure de prise en charge. Choisissez un autre horaire.",
  slot_unavailable_return: "Aucun véhicule n'est disponible pour le retour à cette heure. Choisissez un autre horaire.",
  slot_outside_opening_hours:
    "Ce professionnel n'accepte pas de prise en charge à cet horaire. Choisissez un horaire dans ses heures d'ouverture.",
  slot_outside_opening_hours_return:
    "L'heure de retour est en dehors des heures d'ouverture de ce professionnel. Choisissez un autre horaire de retour.",
  slot_too_soon: 'Cet horaire est trop proche : ce professionnel demande un délai de réservation minimum.',
  slot_too_far: 'Cette date est trop éloignée pour réserver en ligne. Choisissez une date plus proche.',
  slot_no_driver: "Aucun chauffeur n'est disponible sur ce créneau. Choisissez un autre horaire.",
  slot_overbooked_notice:
    'Tous nos véhicules sont occupés sur ce créneau : votre demande sera transmise et confirmée par le chauffeur.',
  choose: 'Choisir',
  selected: 'Sélectionné',
  compute_price: 'Voir le prix',
  address_search_hint: 'Saisissez au moins 3 caractères…',
  address_locked: 'Adresse fixe',
  decrease_quantity: 'Diminuer',
  increase_quantity: 'Augmenter',

  estimated_price: 'Prix estimé',
  distance: 'Distance',
  distance_value: '{distance} km',
  duration: 'Durée estimée',
  route_price: 'Prix du trajet',
  approach_fees: "Frais d'approche",
  waiting_fees: "Frais d'attente",
  handling_fees: 'Prise en charge',
  map_unavailable: "La carte n'a pas pu être chargée.",

  continue: 'Continuer',
  back: 'Retour',
  edit_contact_info: 'Modifier mes informations',

  firstname: 'Prénom',
  lastname: 'Nom',
  email: 'Email',
  phone: 'Téléphone',
  note: 'Note pour le chauffeur (optionnel)',
  // Questionnaire : mêmes libellés que le tunnel classique (source unique DEFAULT_LABELS).
  additional_info: DEFAULT_LABELS.additional_info,
  required_field: DEFAULT_LABELS.required_field,

  pay: 'Payer',
  go_to_payment: 'Continuer vers le paiement',
  request_ride: 'Envoyer la demande',
  deposit_notice: 'Un acompte de {amount} est demandé pour valider la course.',
  imprint_notice: 'Une empreinte bancaire de {amount} sera enregistrée (non débitée).',
  card_save_notice: 'Votre carte sera enregistrée, sans débit immédiat.',
  secure_payment: 'Paiement sécurisé',
  payment_methods_accepted: 'Moyens de paiement acceptés',
  finalizing: 'Finalisation…',
  verifying: 'Vérification du paiement…',
  retry: 'Réessayer',
  payment_redirect_failed:
    'Le paiement a échoué ou a été annulé. Vous pouvez choisir un autre moyen de paiement ou réessayer.',
  payment_redirect_pending:
    'Votre paiement est en cours de validation. Vous recevrez une confirmation dès qu’il sera finalisé.',
  payment_redirect_expired:
    'Votre course a expiré pendant le paiement. Si vous avez été débité, vous serez remboursé automatiquement. Vous pouvez recommencer.',
  payment_redirect_unverified:
    "La confirmation de votre paiement prend plus de temps que prévu. Nous continuons à vérifier : avant de réessayer, vérifiez auprès de votre banque que vous n'avez pas été débité.",

  summary_ride_label: 'Votre course',
  selection_title: 'Votre course',
  total_label: 'Total',
  item_singular: 'élément',
  item_plural: 'éléments',
  show_detail: 'Voir le détail',
  hide_detail: 'Masquer le détail',
  close: 'Fermer',
  vehicle_details: 'Voir le détail',

  success_title: 'Course réservée !',
  success_subtitle: 'Vous recevrez une confirmation par email.',
  request_success_title: 'Demande envoyée !',
  request_success_subtitle: 'Le chauffeur vous recontactera pour confirmer votre course.',
  pending_confirmation_title: 'Paiement reçu !',
  pending_confirmation_subtitle:
    'Votre course est en attente de confirmation par le chauffeur. Vous serez prévenu par email dès qu’elle est confirmée.',
  recap_title: 'Récapitulatif de votre course',
  pickup_label: 'Départ',
  dropoff_label: 'Arrivée',
  pickup_time_label: 'Prise en charge',
  return_time_label: 'Retour',
  recap_note: 'Note pour le chauffeur',
  reference: 'Référence',
  paid_online: 'Réglé en ligne',
  deposit_paid: 'Acompte réglé',
  imprint_saved: 'Empreinte bancaire enregistrée',
  card_saved: 'Carte enregistrée (non débitée)',
  pay_to_driver: 'À régler auprès du chauffeur',
  pay_on_site: 'Réserver et payer sur place',
  pay_on_site_or_online: 'ou',
  error_generic: 'Une erreur est survenue. Veuillez réessayer.',
  error_addresses_required: "Veuillez renseigner le départ et l'arrivée.",
  error_datetime_past: 'La date de prise en charge doit être dans le futur.',
  error_return_required: "Veuillez indiquer l'heure de retour pour un aller-retour.",
  error_return_before_pickup: 'Le retour doit être après le départ.',
  error_return_before_outbound_end: "Le retour doit commencer après la fin estimée de l'aller.",
  error_vehicle_capacity:
    'Ce véhicule ne peut pas accueillir ce nombre de passagers ou de bagages. Choisissez-en un autre ou réduisez le nombre.',
  error_max_passengers: 'La sélection actuelle accepte au maximum {max} passagers.',
  error_max_small_luggage: 'La sélection actuelle accepte au maximum {max} petits bagages.',
  error_max_large_luggage: 'La sélection actuelle accepte au maximum {max} grands bagages.',
  error_selection_capacity:
    'Ce choix ne peut pas accueillir votre nombre de passagers ou de bagages. Réduisez-les pour le sélectionner.',
  error_pickup_outside_zone: 'Cette adresse de départ est en dehors de la zone desservie par ce professionnel.',
  unavailable: 'La réservation de course est indisponible pour le moment.',
};
